import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart,
  Line,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import {
  TrendingUp,
  Calendar,
  BarChart3,
  AlertTriangle,
  Download,
  Search,
  Activity,
  LineChart,
  AreaChart,
  CandlestickChart,
  MoreHorizontal,
  Plus,
  ChevronDown,
  SlidersHorizontal
} from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import CompareDropdown from './CompareDropdown';
import ExportPopup from './ExportPopup';
import FrequencyPopup from './FrequencyPopup';
import Forecast from './Forecast';
import ChartSettings from './ChartSettings';
import GraphIcon from '../../assets/images/graph_icon.png';
import { useMaterialChartOverview, useMaterialChartSeries } from '../../hooks';
import { materialService } from '../../services';
import { ChartSkeleton } from '../Loading';
import { formatCurrency, formatPerUnit, formatUnit } from '../../utils/formatUnit';
import { seriesTfForFrequency } from '../../utils/chartTransforms';
import {
  cleanUnitCode,
  conversionKey,
  conversionParams,
  conversionTarget,
  groupComparisonRequests,
  hasConversionTarget,
  latestChangeFromSeries,
  resolveSeriesDisplay,
} from '../../utils/chartConversion';
import ConversionNote from './ConversionNote';

const timeFrames = [
  { id: '1h', label: '1h' },
  { id: '3h', label: '3h' },
  { id: '1d', label: '1d' },
  { id: '1w', label: '1w' },
  { id: '1m', label: '1m' },
  { id: '3m', label: '3m' },
  { id: '1y', label: '1y' },
  { id: '5y', label: '5y' },
  { id: 'max', label: 'max' },
  { id: 'custom', label: 'custom' }
];
const hiddenTimeFrames = new Set(['1h', '3h', '1d']);
const visibleTimeFrames = timeFrames.filter((tf) => !hiddenTimeFrames.has(tf.id));

// Custom Candlestick Component
const CandlestickBar = (props) => {
  const { payload, x, y, width, height } = props;

  if (!payload || typeof payload.open === 'undefined' || typeof payload.high === 'undefined' ||
      typeof payload.low === 'undefined' || typeof payload.close === 'undefined') {
    return null;
  }

  const { open, high, low, close } = payload;
  const isPositive = close >= open;
  const color = isPositive ? '#10B981' : '#EF4444';

  // Calculate dimensions for proper candlestick
  const candleWidth = Math.max(2, width * 0.6);
  const candleX = x + (width - candleWidth) / 2;
  const wickX = x + width / 2;

  // The chart gives us the coordinates where 'high' value should be positioned
  // We need to calculate other positions relative to that
  const priceRange = high - low;
  if (priceRange === 0) return null;

  // y represents where the 'high' value is positioned
  const highY = y;

  // Calculate positions for other values based on proportional scaling
  const pixelsPerUnit = height / priceRange;

  const lowY = highY + (priceRange * pixelsPerUnit);
  const openY = highY + ((high - open) * pixelsPerUnit);
  const closeY = highY + ((high - close) * pixelsPerUnit);

  // Body dimensions
  const bodyTop = Math.min(openY, closeY);
  const bodyBottom = Math.max(openY, closeY);
  const bodyHeight = Math.max(1, Math.abs(closeY - openY));

  return (
    <g>
      {/* Upper wick */}
      <line
        x1={wickX}
        y1={highY}
        x2={wickX}
        y2={bodyTop}
        stroke={color}
        strokeWidth={1}
      />

      {/* Lower wick */}
      <line
        x1={wickX}
        y1={bodyBottom}
        x2={wickX}
        y2={lowY}
        stroke={color}
        strokeWidth={1}
      />

      {/* Candle body */}
      <rect
        x={candleX}
        y={bodyTop}
        width={candleWidth}
        height={bodyHeight}
        fill={isPositive ? 'transparent' : color}
        stroke={color}
        strokeWidth={1}
        fillOpacity={isPositive ? 0 : 1}
      />
    </g>
  );
};

const CustomTooltip = ({ active, payload, label, chartType, comparisons = [], materialName, materialCode, mode = 'value', getSeriesMeta, t, locale, numberLocale, mainColor = '#8B5CF6', forecastColor: fcColor = '#FCD34D', showForecast = false }) => {
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    const mainMeta = getSeriesMeta ? getSeriesMeta(materialCode) : {};
    // Keine Waehrung der Hauptreihe auf Vergleichsreihen uebertragen: eine Reihe
    // ohne Waehrung (z. B. GWh) zeigt nur ihre Einheit.
    const resolveSeriesMeta = (meta = {}, point = {}) => {
      const currency = formatCurrency(meta?.currency) || formatCurrency(point?.currency);
      const unit = (meta?.unit || point?.value_unit || point?.unit || '').toLowerCase();
      return { currency, unit };
    };
    // Tagesdaten haben nur ein Datum (YYYY-MM-DD = 00:00 UTC). Ohne timeZone 'UTC'
    // zeigte der Tooltip eine Uhrzeit und westlich von UTC den Vortag.
    const formatPointDate = (rawDate) => (rawDate
      ? new Date(rawDate).toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
      : label);
    const formatTooltipValue = (value, currencyValue, unitValue) => {
      if (mode === 'percent') {
        return formatDisplayValue(value, mode, currencyValue, unitValue, 2, numberLocale);
      }
      return formatDisplayValueWithCode(value, currencyValue, unitValue, 2, numberLocale);
    };
    const mainResolvedMeta = resolveSeriesMeta(mainMeta, data);

    return (
      <div style={{
        background: 'rgba(30, 35, 50, 0.98)',
        border: '1px solid rgba(75, 85, 99, 0.3)',
        borderRadius: '8px',
        padding: '8px 12px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.3)',
        color: 'white',
        fontSize: '13px',
        minWidth: '280px'
      }}>
        {chartType === 'candlestick' && data ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', margin: '4px 0' }}>
              <span style={{ color: '#9CA3AF' }}>{t('charts.tooltip.open')}</span>
              <span style={{ color: '#3B82F6', fontWeight: '500' }}>{formatTooltipValue(data.open, mainResolvedMeta.currency, mainResolvedMeta.unit)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', margin: '4px 0' }}>
              <span style={{ color: '#9CA3AF' }}>{t('charts.tooltip.high')}</span>
              <span style={{ color: '#10B981', fontWeight: '500' }}>{formatTooltipValue(data.high, mainResolvedMeta.currency, mainResolvedMeta.unit)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', margin: '4px 0' }}>
              <span style={{ color: '#9CA3AF' }}>{t('charts.tooltip.low')}</span>
              <span style={{ color: '#EF4444', fontWeight: '500' }}>{formatTooltipValue(data.low, mainResolvedMeta.currency, mainResolvedMeta.unit)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', margin: '4px 0' }}>
              <span style={{ color: '#9CA3AF' }}>{t('charts.tooltip.close')}</span>
              <span style={{ color: mainColor, fontWeight: '500' }}>{formatTooltipValue(data.close, mainResolvedMeta.currency, mainResolvedMeta.unit)}</span>
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              margin: '8px 0 4px 0',
              paddingTop: '8px',
              borderTop: '1px solid rgba(75, 85, 99, 0.3)'
            }}>
              <span style={{ color: '#9CA3AF' }}>{t('charts.tooltip.volume')}</span>
              <span style={{ color: '#06B6D4', fontWeight: '500' }}>{data.volume?.toLocaleString(locale)}</span>
            </div>
          </div>
        ) : (
          <div>
            {/* Main value with color indicator and timestamp */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '6px',
              paddingBottom: comparisons.length > 0 ? '6px' : '0',
              borderBottom: comparisons.length > 0 ? '1px solid rgba(75, 85, 99, 0.2)' : 'none'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: mainColor,
                  flexShrink: 0
                }} />
                <span style={{ color: '#E5E7EB', fontSize: '13px', fontWeight: '400' }}>
                  {materialName || t('charts.material')}
                </span>
              </div>
              <div style={{ textAlign: 'right', marginLeft: '12px' }}>
                <div style={{ color: '#E5E7EB', fontWeight: '500', fontSize: '14px' }}>
                  {/* Wert der Hauptreihe aus der Zeile lesen, nicht payload[0]: Recharts
                      filtert null-Eintraege, payload[0] war dann die News-Anzahl oder
                      eine Vergleichsreihe und stand als Kurs im Tooltip. */}
                  {formatTooltipValue(data?.value, mainResolvedMeta.currency, mainResolvedMeta.unit)}
                </div>
                <div style={{ color: '#9CA3AF', fontSize: '11px', marginTop: '2px' }}>
                  {formatPointDate(data?.date)}
                </div>
              </div>
            </div>

            {/* Main forecast value */}
            {showForecast && data?.forecast_value != null && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '6px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                  <div style={{
                    width: '8px',
                    height: '2px',
                    borderTop: `2px dashed ${fcColor}`,
                    flexShrink: 0
                  }} />
                  <span style={{ color: fcColor, fontSize: '12px', fontWeight: '400' }}>
                    {(materialName || t('charts.material'))} ({t('charts.forecast')})
                  </span>
                </div>
                <div style={{ textAlign: 'right', marginLeft: '12px' }}>
                  <div style={{ color: fcColor, fontWeight: '500', fontSize: '14px' }}>
                    {formatTooltipValue(data.forecast_value, mainResolvedMeta.currency, mainResolvedMeta.unit)}
                  </div>
                </div>
              </div>
            )}

            {/* Show comparison values */}
            {comparisons.map((comparison, index) => {
              const compValue = data?.[`comparison_${index}_value`];
              const compForecastValue = data?.[`comparison_${index}_forecast_value`];
              const compMeta = getSeriesMeta ? getSeriesMeta(comparison.code, comparison.id) : {};
              const compResolvedMeta = resolveSeriesMeta(
                compMeta,
                {
                  currency: data?.[`comparison_${index}_currency`],
                  value_unit: data?.[`comparison_${index}_value_unit`],
                  unit: data?.[`comparison_${index}_unit`],
                }
              );
              return (
                <React.Fragment key={comparison.id}>
                  {compValue !== null && compValue !== undefined && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '6px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                        <div style={{
                          width: '8px',
                          height: '8px',
                          borderRadius: '50%',
                          backgroundColor: comparison.color,
                          flexShrink: 0
                        }} />
                        <span style={{ color: '#E5E7EB', fontSize: '13px', fontWeight: '400' }}>
                          {comparison.name}
                        </span>
                      </div>
                      <div style={{ textAlign: 'right', marginLeft: '12px' }}>
                        <div style={{ color: '#E5E7EB', fontWeight: '500', fontSize: '14px' }}>
                          {formatTooltipValue(compValue, compResolvedMeta.currency, compResolvedMeta.unit)}
                        </div>
                        <div style={{ color: '#9CA3AF', fontSize: '11px', marginTop: '2px' }}>
                          {formatPointDate(data?.date)}
                        </div>
                      </div>
                    </div>
                  )}
                  {showForecast && compForecastValue != null && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '6px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                        <div style={{
                          width: '8px',
                          height: '2px',
                          borderTop: `2px dashed ${fcColor}`,
                          flexShrink: 0
                        }} />
                        <span style={{ color: fcColor, fontSize: '12px', fontWeight: '400' }}>
                          {comparison.name} ({t('charts.forecast')})
                        </span>
                      </div>
                      <div style={{ textAlign: 'right', marginLeft: '12px' }}>
                        <div style={{ color: fcColor, fontWeight: '500', fontSize: '14px' }}>
                          {formatTooltipValue(compForecastValue, compResolvedMeta.currency, compResolvedMeta.unit)}
                        </div>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    );
  }
  return null;
};

const currencySymbols = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
};
const forecastColor = '#FCD34D'; // yellow for forecast

const NUMBER_FORMAT_STORAGE_KEY = 'df_number_format';
const NUMBER_FORMAT_OPTIONS = new Set(['auto', 'us', 'eu']);

const normaliseNumberFormat = (value) => {
  const lower = String(value || '').toLowerCase();
  return NUMBER_FORMAT_OPTIONS.has(lower) ? lower : 'auto';
};

const getNumberLocale = (numberFormat = 'auto', language = 'en') => {
  const format = normaliseNumberFormat(numberFormat);
  if (format === 'us') return 'en-US';
  if (format === 'eu') return 'de-DE';
  const lower = String(language || 'en').toLowerCase();
  return lower.startsWith('de') ? 'de-DE' : 'en-US';
};

const formatDisplayValue = (value, mode, currency, unit, maxFraction = 2, numberLocale = 'en-US') => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  if (mode === 'percent') {
    return `${value.toFixed(2)}%`;
  }
  const symbol = currencySymbols[currency] || '';
  const formatted = Number(value).toLocaleString(numberLocale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxFraction,
  });
  const unitLabel = formatUnit(unit);
  return `${symbol}${formatted}${unitLabel ? ` ${unitLabel}` : ''}`.trim();
};

function formatDisplayValueWithCode(value, currency, unit, maxFraction = 2, numberLocale = 'en-US') {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const formatted = Number(value).toLocaleString(numberLocale, {
    minimumFractionDigits: maxFraction,
    maximumFractionDigits: maxFraction,
  });
  // Rohe Einheitencodes (brl_US, bushel_US, troyounce ...) lesbar anzeigen.
  const suffix = formatPerUnit(currency, unit);
  return suffix ? `${formatted} ${suffix}` : formatted;
}

const formatYAxisValue = (value, mode = 'value', currency = '', unit = '', numberLocale = 'en-US') => {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  if (mode === 'percent') {
    return `${value.toFixed(2)}%`;
  }
  if (value >= 1000000) {
    return `${currencySymbols[currency] || ''}${(value / 1000000).toLocaleString(numberLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}M`;
  } else if (value >= 1000) {
    return `${currencySymbols[currency] || ''}${(value / 1000).toLocaleString(numberLocale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}k`;
  }
  const unitLabel = formatUnit(unit);
  return `${currencySymbols[currency] || ''}${Math.round(value).toLocaleString(numberLocale)}${unitLabel ? ` ${unitLabel}` : ''}`.trim();
};

// Chart History Navigator Component
const ChartHistoryNavigator = ({ fullData, selectionRange, onSelectionChange, selectedTimeFrame, onInteractionChange, mainColor = '#8B5CF6' }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(null); // 'left' or 'right'
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartRange, setDragStartRange] = useState({ start: 0, end: 0 });
  const navigatorRef = useRef(null);

  const handleMouseDown = useCallback((e, action) => {
    e.preventDefault();
    const rect = navigatorRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    
    setDragStartX(clickX);
    setDragStartRange({ ...selectionRange });
    
    if (action === 'drag') {
      setIsDragging(true);
    } else if (action === 'resize-left' || action === 'resize-right') {
      setIsResizing(action);
    }
    onInteractionChange?.(true);
  }, [selectionRange, onInteractionChange]);

  const handleMouseMove = useCallback((e) => {
    if (!navigatorRef.current || (!isDragging && !isResizing)) return;
    
    const rect = navigatorRef.current.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const deltaX = currentX - dragStartX;
    const containerWidth = rect.width;
    const deltaPercent = deltaX / containerWidth;
    
    if (isDragging) {
      // Dragging the entire selection
      const rangeWidth = dragStartRange.end - dragStartRange.start;
      let newStart = Math.max(0, Math.min(1 - rangeWidth, dragStartRange.start + deltaPercent));
      let newEnd = newStart + rangeWidth;
      
      // Ensure we don't go out of bounds
      if (newEnd > 1) {
        newEnd = 1;
        newStart = newEnd - rangeWidth;
      }
      
      onSelectionChange({ start: newStart, end: newEnd });
    } else if (isResizing === 'resize-left') {
      // Resizing from the left edge - maintain minimum width
      const minWidth = Math.max(0.02, 20 / navigatorRef.current.offsetWidth); // 2% or 20px minimum
      const newStart = Math.max(0, Math.min(dragStartRange.end - minWidth, dragStartRange.start + deltaPercent));
      onSelectionChange({ start: newStart, end: dragStartRange.end });
    } else if (isResizing === 'resize-right') {
      // Resizing from the right edge - maintain minimum width
      const minWidth = Math.max(0.02, 20 / navigatorRef.current.offsetWidth); // 2% or 20px minimum
      const newEnd = Math.min(1, Math.max(dragStartRange.start + minWidth, dragStartRange.end + deltaPercent));
      onSelectionChange({ start: dragStartRange.start, end: newEnd });
    }
  }, [isDragging, isResizing, dragStartX, dragStartRange, onSelectionChange]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(null);
    onInteractionChange?.(false);
  }, [onInteractionChange]);

  React.useEffect(() => {
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  const selectionStyle = {
    left: `${selectionRange.start * 100}%`,
    width: `${Math.max(2, (selectionRange.end - selectionRange.start) * 100)}%`, // Minimum 2% width
    cursor: isDragging ? 'grabbing' : 'grab',
    transition: isDragging || isResizing ? 'none' : 'all 0.3s ease' // Smooth transition when not dragging
  };


  return (
    <div className="chart-history-navigator">
      
      <div
        ref={navigatorRef}
        className="chart-history-overview"
      >
        {/* Overview Chart */}
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={fullData} margin={{ top: 10, right: 20, left: 20, bottom: 20 }}>
            <Area
              type="stepAfter"
              connectNulls
              dataKey="value"
              stroke={mainColor}
              strokeWidth={1}
              fill={mainColor + '4d'}
              dot={false}
            />
            <XAxis 
              dataKey="monthLabel"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6B7280', fontSize: 10 }}
              interval="preserveStartEnd"
            />
            <YAxis hide />
          </ComposedChart>
        </ResponsiveContainer>

        {/* Selection Overlay */}
        <div
          className={`chart-history-slider ${isDragging ? 'dragging' : ''}`}
          style={selectionStyle}
        >
          <div
            className="chart-history-handle left"
            onMouseDown={(e) => handleMouseDown(e, 'resize-left')}
          />

          <div
            className="chart-history-handle right"
            onMouseDown={(e) => handleMouseDown(e, 'resize-right')}
          />
          
          <div 
            style={{
              position: 'absolute',
              top: '0',
              left: '12px',
              right: '12px',
              height: '100%',
              cursor: isDragging ? 'grabbing' : 'grab'
            }}
            onMouseDown={(e) => handleMouseDown(e, 'drag')}
          />
        </div>

        {/* Dimmed overlay for non-selected areas */}
        <div style={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          background: `linear-gradient(to right,
            rgba(17, 24, 39, 0.4) 0%,
            rgba(17, 24, 39, 0.4) ${selectionRange.start * 100}%,
            transparent ${selectionRange.start * 100}%,
            transparent ${selectionRange.end * 100}%,
            rgba(17, 24, 39, 0.4) ${selectionRange.end * 100}%,
            rgba(17, 24, 39, 0.4) 100%)`,
          pointerEvents: 'none',
          zIndex: 5
        }} />
      </div>

    </div>
  );
};

// Stabiler Default, sonst laufen Effekte mit [comparisons] bei jedem Rendern
const NO_COMPARISONS = [];

export default function MainChart({ comparisons = NO_COMPARISONS, onAddComparison, maxComparisons = 3, materialCode, material, currentMaterialCode, onHeaderDataChange, onComparisonColorsChange, onComparisonHeaderDataChange }) {
  const { t, i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('de')
    ? 'de-DE'
    : (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase().startsWith('zh')
      ? 'zh-CN'
      : 'en-US';
  const [selectedTimeFrame, setSelectedTimeFrame] = useState('1y');
  const [selectedChartType, setSelectedChartType] = useState('area');
  const [selectedFrequency, setSelectedFrequency] = useState('daily');
  const [chartMode, setChartMode] = useState('value'); // value | percent
  const [chartSettingsState, setChartSettingsState] = useState(null);
  const [numberFormat, setNumberFormat] = useState(() => {
    if (typeof window === 'undefined') return 'auto';
    return normaliseNumberFormat(window.localStorage.getItem(NUMBER_FORMAT_STORAGE_KEY));
  });
  const numberLocale = useMemo(
    () => getNumberLocale(numberFormat, i18n.resolvedLanguage || i18n.language || 'en'),
    [numberFormat, i18n.resolvedLanguage, i18n.language]
  );
  const [forecastOptions, setForecastOptions] = useState({ horizon: '1m', includeNews: true, scenario: '' });
  const [forecastDataMap, setForecastDataMap] = useState(() => {
    try {
      const cached = sessionStorage.getItem(`df_forecast_${materialCode}`);
      if (cached) return JSON.parse(cached);
    } catch {}
    return {};
  });
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastError, setForecastError] = useState(null);
  const [forecastRequested, setForecastRequested] = useState(() => {
    try { return !!sessionStorage.getItem(`df_forecast_${materialCode}`); } catch { return false; }
  });
  const [forecastRequestNonce, setForecastRequestNonce] = useState(0);

  useEffect(() => {
    if (hiddenTimeFrames.has(selectedTimeFrame)) {
      setSelectedTimeFrame('1w');
    }
  }, [selectedTimeFrame]);
  const [showNews, setShowNews] = useState(true);
  const [showValue, setShowValue] = useState(true);
  const [showForecast, setShowForecast] = useState(() => {
    try { return !!sessionStorage.getItem(`df_forecast_${materialCode}`); } catch { return false; }
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  // Uebernommener eigener Zeitraum als Kalendertage (YYYY-MM-DD) plus die dazu
  // gesetzte Navigator-Auswahl. Solange diese Auswahl gilt, fragt der Chart genau
  // diese Tage ab statt der aus Prozentwerten zurueckgerechneten Zeitpunkte.
  const [appliedCustomRange, setAppliedCustomRange] = useState(null);
  const [showExportPopup, setShowExportPopup] = useState(false);
  const [showForecastPopup, setShowForecastPopup] = useState(false);
  const [showSettingsPopup, setShowSettingsPopup] = useState(false);
  const [showFrequencyPopup, setShowFrequencyPopup] = useState(false);
  const chartAreaRef = useRef(null);

  // Fetch historical overview data from API
  const {
    data: fullHistoricalDataRaw,
    loading: overviewLoading,
    error: overviewError
  } = useMaterialChartOverview(materialCode, {
    enabled: true,
    cache: true,
    cacheTTL: 10 * 60 * 1000, // Cache for 10 minutes (static historical data)
  });

  // Guardrail: never let the regular chart timeline extend into future dates.
  // Forecast is rendered separately and can still project forward.
  const fullHistoricalData = useMemo(() => {
    if (!Array.isArray(fullHistoricalDataRaw) || fullHistoricalDataRaw.length === 0) return [];
    const now = Date.now();
    return fullHistoricalDataRaw.filter((point) => {
      const ts = Number(point?.timestamp);
      if (Number.isFinite(ts)) return ts <= now;
      const d = point?.date ? new Date(point.date).getTime() : NaN;
      return Number.isFinite(d) ? d <= now : false;
    });
  }, [fullHistoricalDataRaw]);

  // Navigator selection state (as percentage of full data)
  const [selectionRange, setSelectionRange] = useState({ start: 0.75, end: 1.0 }); // Default to last 25%
  const [isNavigatorInteracting, setIsNavigatorInteracting] = useState(false);
  const [fullRangeSeriesData, setFullRangeSeriesData] = useState(null);
  const fullRangeRequestRef = useRef(null);

  // Gespeicherte Einstellung (Farbe, Zielwaehrung/-einheit) einer Reihe
  const findSetting = useCallback((codeOrId, idFallback = null) => (chartSettingsState || []).find(
    (s) => s.code === codeOrId || s.id === codeOrId || s.id === idFallback || s.code === idFallback
  ), [chartSettingsState]);

  // Umrechnung macht das Backend (Kurs des jeweiligen Tages); hier nur das Ziel.
  const mainTarget = useMemo(() => conversionTarget(findSetting(materialCode)), [findSetting, materialCode]);
  const mainConversionKey = conversionKey(mainTarget);
  const fullRangeKey = `${materialCode}|${mainConversionKey}`;
  
  // Function to determine the closest timeframe based on selection range
  const getTimeframeFromSelection = useCallback((selectionRange) => {
    if (!fullHistoricalData || !fullHistoricalData.length) return 'custom';
    
    const totalTimeSpan = fullHistoricalData[fullHistoricalData.length - 1].timestamp - fullHistoricalData[0].timestamp;
    const selectedTimeSpan = (selectionRange.end - selectionRange.start) * totalTimeSpan;
    
    // Convert to days for easier comparison
    const selectedDays = selectedTimeSpan / (24 * 60 * 60 * 1000);
    
    // Define timeframe thresholds (in days) with some tolerance
    const timeframes = [
      { id: '1h', days: 1/24, tolerance: 0.1 },
      { id: '3h', days: 3/24, tolerance: 0.2 },
      { id: '1d', days: 1, tolerance: 0.3 },
      { id: '1w', days: 7, tolerance: 2 },
      { id: '1m', days: 30, tolerance: 5 },
      { id: '3m', days: 90, tolerance: 10 },
      { id: '1y', days: 365, tolerance: 30 },
      { id: '5y', days: 365 * 5, tolerance: 60 },
    ];
    
    // Check for exact match first (max = full range)
    if (selectionRange.start === 0 && selectionRange.end === 1) {
      return 'max';
    }
    
    // Find closest matching timeframe
    for (const timeframe of timeframes) {
      if (Math.abs(selectedDays - timeframe.days) <= timeframe.tolerance) {
        return timeframe.id;
      }
    }
    
    return 'custom'; // No standard timeframe matches
  }, [fullHistoricalData]);

  // Handle selection change from navigator
  const handleSelectionChange = useCallback((newSelectionRange) => {
    setSelectionRange(newSelectionRange);
    
    // Update timeframe to match selection if it matches a standard timeframe
    const matchingTimeframe = getTimeframeFromSelection(newSelectionRange);
    if (matchingTimeframe !== 'custom' && matchingTimeframe !== selectedTimeFrame) {
      setSelectedTimeFrame(matchingTimeframe);
    }
  }, [getTimeframeFromSelection, selectedTimeFrame]);
  
  
  
  // Calculate date range based on selection
  const { startDate, endDate } = useMemo(() => {
    if (!fullHistoricalData || !fullHistoricalData.length) return { startDate: new Date(), endDate: new Date() };

    // Eigener Zeitraum: Datepicker liefert lokale Mitternacht; ueber die Prozent-
    // Auswahl und toISOString() wurde daraus oestlich von UTC der Vortag.
    if (selectedTimeFrame === 'custom' && appliedCustomRange && appliedCustomRange.selection === selectionRange) {
      return {
        startDate: new Date(`${appliedCustomRange.start}T00:00:00Z`),
        endDate: new Date(`${appliedCustomRange.end}T00:00:00Z`),
      };
    }

    const totalSpan = fullHistoricalData[fullHistoricalData.length - 1].timestamp - fullHistoricalData[0].timestamp;
    const startTimestamp = fullHistoricalData[0].timestamp + (selectionRange.start * totalSpan);
    const endTimestamp = fullHistoricalData[0].timestamp + (selectionRange.end * totalSpan);

    return {
      startDate: new Date(startTimestamp),
      endDate: new Date(endTimestamp)
    };
  }, [fullHistoricalData, selectionRange, selectedTimeFrame, appliedCustomRange]);

  // Build an immediate local preview while dragging the history navigator.
  const liveRangeSeriesData = useMemo(() => {
    if (!fullHistoricalData || fullHistoricalData.length === 0) return null;
    const fullRangeReady =
      fullRangeSeriesData?.key === fullRangeKey &&
      Array.isArray(fullRangeSeriesData?.data) &&
      fullRangeSeriesData.data.length > 0;
    if (!fullRangeReady) return null;

    const totalSpan = fullHistoricalData[fullHistoricalData.length - 1].timestamp - fullHistoricalData[0].timestamp;
    const startTimestamp = fullHistoricalData[0].timestamp + (selectionRange.start * totalSpan);
    const endTimestamp = fullHistoricalData[0].timestamp + (selectionRange.end * totalSpan);
    const sourceData = fullRangeSeriesData?.data || [];

    const preview = sourceData
      .filter((point) => {
        const timestamp = new Date(point.date).getTime();
        return timestamp >= startTimestamp && timestamp <= endTimestamp;
      })
      .map((point) => ({
        ...point,
        xAxisLabel: point.xAxisLabel || point.monthLabel || point.date,
        open: point.open ?? point.value,
        high: point.high ?? point.value,
        low: point.low ?? point.value,
        close: point.close ?? point.value,
        volume: Number.isFinite(Number(point?.volume)) ? Number(point.volume) : 0,
      }));

    return { data: preview, meta: fullRangeSeriesData.meta };
  }, [fullHistoricalData, selectionRange, fullRangeSeriesData, fullRangeKey]);

  // Punktdichte folgt der Frequenz, nicht dem Zeitraum-Button (Daily = ein Punkt
  // je Handelstag, auch in 1J/5J/Max). Frueher: 1J -> tf=1m, 5J -> 3m, Max -> 1y.
  const seriesTf = seriesTfForFrequency(selectedFrequency);

  // Fetch main chart series data from API
  const {
    data: chartSeriesData,
    loading: seriesLoading,
    error: seriesError
  } = useMaterialChartSeries(materialCode, {
    start: startDate.toISOString().split('T')[0],
    end: endDate.toISOString().split('T')[0],
    tf: seriesTf,
    // Zielwaehrung/-einheit (fehlt = Original); landet auch im Cache-Key
    ...conversionParams(mainTarget),
  }, {
    // Eigener Zeitraum laedt ebenfalls (vorher bei 'custom' immer deaktiviert)
    enabled: !!startDate && !!endDate
      && (selectedTimeFrame !== 'custom' || (customStartDate && customEndDate))
      && !isNavigatorInteracting && !overviewLoading && fullHistoricalData.length > 0,
    cache: true,
    cacheTTL: 5 * 60 * 1000, // Cache for 5 minutes
    dependencies: [startDate, endDate, selectedTimeFrame],
  });

  const isFullRangeSeriesReady = useMemo(
    () =>
      fullRangeSeriesData?.key === fullRangeKey &&
      Array.isArray(fullRangeSeriesData?.data) &&
      fullRangeSeriesData.data.length > 0,
    [fullRangeSeriesData, fullRangeKey]
  );

  // Gesamte Historie (Tageswerte, gleiche Umrechnung) fuer die Live-Vorschau beim
  // Ziehen des Navigators und fuer den Kopf bei aktiver Umrechnung. Laeuft je
  // Material + Ziel genau einmal; eine veraltete Antwort wird verworfen.
  useEffect(() => {
    if (!materialCode || !fullHistoricalData?.length) return;
    if (!chartSeriesData?.data?.length) return; // First load selected timeframe
    if (fullRangeSeriesData?.key === fullRangeKey || fullRangeRequestRef.current === fullRangeKey) return;

    const firstDate = fullHistoricalData[0]?.date;
    const lastDate = fullHistoricalData[fullHistoricalData.length - 1]?.date;
    if (!firstDate || !lastDate) return;

    const requestKey = fullRangeKey;
    fullRangeRequestRef.current = requestKey;
    materialService.getChartSeries(materialCode, {
      start: firstDate,
      end: lastDate,
      tf: '1d',
      ...conversionParams(mainTarget),
    })
      .then((response) => {
        if (fullRangeRequestRef.current !== requestKey) return;
        setFullRangeSeriesData({
          key: requestKey,
          materialCode,
          data: Array.isArray(response?.data) ? response.data : [],
          meta: response?.meta || null,
        });
      })
      .catch((error) => {
        console.warn('[MainChart] Failed to preload full range series:', error);
      })
      .finally(() => {
        if (fullRangeRequestRef.current === requestKey) fullRangeRequestRef.current = null;
      });
    // mainTarget steckt in fullRangeKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialCode, fullRangeKey, fullHistoricalData, chartSeriesData, fullRangeSeriesData?.key]);

  const effectiveChartSeriesData = useMemo(() => {
    if (isNavigatorInteracting && isFullRangeSeriesReady && liveRangeSeriesData?.data?.length) {
      return liveRangeSeriesData;
    }
    return chartSeriesData;
  }, [chartSeriesData, isNavigatorInteracting, liveRangeSeriesData, isFullRangeSeriesReady]);

  // Ziel, mit dem die angezeigten Daten angefragt wurden. Bis die Antwort fuer
  // ein neues Ziel da ist, bleiben Daten und Beschriftung beim alten Stand
  // (sonst kurz "Umrechnung nicht moeglich" fuer noch alte Daten).
  const seriesDataTargetRef = useRef({ data: undefined, target: mainTarget });
  if (seriesDataTargetRef.current.data !== chartSeriesData) {
    seriesDataTargetRef.current = { data: chartSeriesData, target: mainTarget };
  }
  const mainDataTarget = effectiveChartSeriesData === liveRangeSeriesData
    ? mainTarget
    : seriesDataTargetRef.current.target;

  // Fetch comparison data when comparisons exist
  const [comparisonData, setComparisonData] = useState(null);
  // Je Vergleichsreihe: meta aus chart/compare und das dabei angefragte Ziel
  const [comparisonMetaByCode, setComparisonMetaByCode] = useState({});
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);
  // Waehrung/Einheit der Vergleichsreihen (aus /general) fuer Tooltip und Achse
  const [comparisonGeneralByCode, setComparisonGeneralByCode] = useState({});

  useEffect(() => {
    let cancelled = false;
    const missing = comparisons
      .map((c) => c?.code)
      .filter((code) => code && !comparisonGeneralByCode[code]);
    if (!missing.length) return undefined;
    Promise.all(
      missing.map((code) =>
        materialService.getGeneral(code).then((general) => [code, general]).catch(() => [code, null])
      )
    ).then((entries) => {
      if (cancelled) return;
      setComparisonGeneralByCode((prev) => {
        const next = { ...prev };
        entries.forEach(([code, general]) => {
          if (general) next[code] = { currency: general.currency, value_unit: general.value_unit };
        });
        return next;
      });
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisons]);

  const normaliseSeries = useCallback((rawData = []) => {
    if (!rawData || rawData.length === 0) return [];
    const now = Date.now();
    return [...rawData]
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .filter((item) => {
        const ts = item?.date ? new Date(item.date).getTime() : NaN;
        return Number.isFinite(ts) ? ts <= now : false;
      })
      .map((item) => {
        const resolvedValue = item.value ?? item.close ?? item.open ?? item.high ?? item.low ?? null;
        const resolvedDate = item?.date ? new Date(item.date) : null;
        const safeDate = resolvedDate && !Number.isNaN(resolvedDate.getTime())
          ? resolvedDate.toISOString().split('T')[0]
          : item?.date;
        return {
          ...item,
          date: safeDate,
          value: resolvedValue,
          open: item.open ?? resolvedValue,
          high: item.high ?? resolvedValue,
          low: item.low ?? resolvedValue,
          close: item.close ?? resolvedValue,
          volume: Number.isFinite(Number(item?.volume)) ? Number(item.volume) : 0,
          volumeNormalized: 0,
        };
      });
  }, []);

  // Patch recharts-wrapper tabindex to -1 so Safari never fires a focus ring on mouse click.
  // tabindex="-1" keeps the element programmatically focusable (screen readers still work via
  // role="application") but prevents it from receiving focus via mouse or Tab key.
  useEffect(() => {
    const el = chartAreaRef.current;
    if (!el) return;
    const patch = () => {
      el.querySelectorAll('.recharts-wrapper').forEach((w) => {
        w.setAttribute('tabindex', '-1');
      });
    };
    patch();
    const observer = new MutationObserver(patch);
    observer.observe(el, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Fetch forecast data for main + comparison materials once requested
  useEffect(() => {
    const loadForecast = async () => {
      if (!materialCode || !forecastRequested) {
        return;
      }
      try {
        setForecastLoading(true);
        setForecastError(null);
        const targetCodes = [
          materialCode,
          ...comparisons.map((c) => c.code).filter(Boolean),
        ];
        const uniqueCodes = Array.from(new Set(targetCodes));
        const responses = await Promise.all(
          uniqueCodes.map(async (code) => {
            try {
              const res = await materialService.getForecast(code, {
                horizon: forecastOptions?.horizon || '1m',
                frequency: selectedFrequency === 'weekly' ? 'weekly' : 'daily',
                include_news: forecastOptions?.includeNews,
                scenario: forecastOptions?.scenario,
              });
              return [code, Array.isArray(res?.data) ? res.data : []];
            } catch {
              return [code, []];
            }
          })
        );
        const nextMap = {};
        responses.forEach(([code, data]) => {
          nextMap[code] = data;
        });
        setForecastDataMap(nextMap);
        try { sessionStorage.setItem(`df_forecast_${materialCode}`, JSON.stringify(nextMap)); } catch {}
      } catch (e) {
        setForecastError(e);
        setForecastDataMap({});
      } finally {
        setForecastLoading(false);
      }
    };
    loadForecast();
  }, [materialCode, comparisons, forecastOptions, selectedFrequency, forecastRequested, forecastRequestNonce]);

  // Quellwaehrung/-einheit einer Reihe (ohne Umrechnung). Kein 'USD'-Fallback:
  // Reihen ohne Waehrung (GWh, Index, %) zeigen nur die Einheit.
  const getSeriesSource = useCallback((codeOrId, idFallback = null) => {
    const setting = findSetting(codeOrId, idFallback);
    if (codeOrId === materialCode) {
      return {
        currency: formatCurrency(material?.currency) || formatCurrency(setting?.baseCurrency),
        unit: cleanUnitCode(material?.value_unit) || cleanUnitCode(setting?.baseUnit),
      };
    }
    // Vergleichsreihe: eigene Waehrung/Einheit aus /general, nicht die der
    // Hauptreihe (sonst z. B. "USD/t" fuer eine GWh-Reihe).
    const comparison = comparisons.find((c) => c?.code === codeOrId || c?.id === codeOrId || c?.id === idFallback);
    const general = comparison ? comparisonGeneralByCode[comparison.code] : null;
    return {
      currency: formatCurrency(general?.currency) || formatCurrency(setting?.baseCurrency),
      unit: cleanUnitCode(general?.value_unit) || cleanUnitCode(setting?.baseUnit),
    };
  }, [findSetting, material, materialCode, comparisons, comparisonGeneralByCode]);

  // Ziel je Vergleichsreihe (null = Original) und ein stabiler Schluessel dafuer
  const comparisonTargets = useMemo(() => {
    const out = {};
    comparisons.forEach((c) => {
      if (c?.code) out[c.code] = conversionTarget(findSetting(c.code, c.id));
    });
    return out;
  }, [comparisons, findSetting]);
  const comparisonTargetsKey = useMemo(
    () => comparisons.map((c) => `${c?.code}:${conversionKey(comparisonTargets[c?.code])}`).join(','),
    [comparisons, comparisonTargets]
  );

  /**
   * Beschriftung einer Reihe: Waehrung/Einheit aus meta der Antwort, wenn das
   * Backend umgerechnet hat, sonst die Originalwerte (Daten sind dann ebenfalls
   * unveraendert).
   */
  const getSeriesMeta = useCallback((codeOrId, idFallback = null) => {
    const setting = findSetting(codeOrId, idFallback) || {};
    const source = getSeriesSource(codeOrId, idFallback);
    const isMain = codeOrId === materialCode;
    const comparison = isMain ? null : comparisons.find((c) => c?.code === codeOrId || c?.id === codeOrId || c?.id === idFallback);
    const code = isMain ? materialCode : comparison?.code;
    const comparisonResult = isMain ? null : comparisonMetaByCode[code];
    const target = isMain
      ? mainDataTarget
      : (comparisonResult?.target || { currency: null, unit: null });
    const responseMeta = isMain ? effectiveChartSeriesData?.meta : comparisonResult?.meta;
    const display = resolveSeriesDisplay({
      sourceCurrency: source.currency,
      sourceUnit: source.unit,
      target,
      meta: responseMeta,
    });
    return {
      ...setting,
      name: setting.name || (isMain ? material?.name : comparison?.name),
      baseCurrency: display.sourceCurrency,
      baseUnit: display.sourceUnit,
      currency: display.currency,
      unit: display.unit ? display.unit.toLowerCase() : '',
      display,
      responseMeta: responseMeta || null,
    };
  }, [findSetting, getSeriesSource, materialCode, comparisons, mainDataTarget, effectiveChartSeriesData, comparisonMetaByCode, material]);

  const mainMeta = useMemo(() => getSeriesMeta(materialCode), [getSeriesMeta, materialCode]);
  const mainColor = useMemo(() => mainMeta?.color || '#8B5CF6', [mainMeta]);
  const displayComparisons = useMemo(
    () => comparisons.map((comparison) => {
      const compMeta = getSeriesMeta(comparison.code, comparison.id);
      return { ...comparison, color: compMeta?.color || comparison.color };
    }),
    [comparisons, getSeriesMeta]
  );
  useEffect(() => {
    if (typeof onComparisonColorsChange !== 'function') return;
    const colorMap = {};
    displayComparisons.forEach((comparison) => {
      if (comparison?.id !== null && comparison?.id !== undefined) {
        colorMap[comparison.id] = comparison.color || '#6B7280';
      }
    });
    onComparisonColorsChange(colorMap);
  }, [onComparisonColorsChange, displayComparisons]);
  const yTickFormatter = useCallback(
    (v) => formatYAxisValue(v, chartMode, mainMeta.currency, mainMeta.unit, numberLocale),
    [chartMode, mainMeta.currency, mainMeta.unit, numberLocale]
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(NUMBER_FORMAT_STORAGE_KEY, normaliseNumberFormat(numberFormat));
  }, [numberFormat]);

  useEffect(() => {
    let cancelled = false;
    const fetchComparisonData = async () => {
      // If no comparisons or no valid date range, clear comparison data
      const customIncomplete = selectedTimeFrame === 'custom' && !(customStartDate && customEndDate);
      if (isNavigatorInteracting || comparisons.length === 0 || !startDate || !endDate || customIncomplete) {
        setComparisonData(null);
        setComparisonMetaByCode((prev) => (Object.keys(prev).length ? {} : prev));
        setComparisonLoading(false);
        return;
      }

      try {
        setComparisonLoading(true);
        setComparisonError(null);

        // chart/compare kennt ein Ziel je Request: Vergleichsreihen nach Ziel
        // gruppieren (ohne eigene Einstellungen bleibt es bei einem Request).
        const groups = groupComparisonRequests(comparisons, (c) => comparisonTargets[c.code]);
        const responses = await Promise.all(groups.map((group) => (
          materialService.getChartComparison(materialCode, {
            codes: group.codes.join(','),
            start: startDate.toISOString(),
            end: endDate.toISOString(),
            tf: seriesTf,
            ...group.params,
          })
        )));
        if (cancelled) return;

        const series = {};
        const meta = {};
        responses.forEach((response, idx) => {
          groups[idx].codes.forEach((code) => {
            if (response?.series?.[code]) series[code] = response.series[code];
            meta[code] = { meta: response?.meta?.[code] || null, target: groups[idx].target };
          });
        });
        setComparisonData(series);
        setComparisonMetaByCode(meta);
      } catch (error) {
        if (cancelled) return;
        console.error('[MainChart] Failed to fetch comparison data:', error);
        setComparisonError(error);
      } finally {
        if (!cancelled) setComparisonLoading(false);
      }
    };

    fetchComparisonData();
    return () => { cancelled = true; };
    // comparisonTargets steckt in comparisonTargetsKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparisons, comparisonTargetsKey, materialCode, startDate, endDate, selectedTimeFrame, seriesTf, isNavigatorInteracting, customStartDate, customEndDate]);

  // Helper function to get ISO week number
  const getISOWeek = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return weekNo;
  };

  // Aggregate data based on frequency
  const aggregateDataByFrequency = useCallback((rawData, frequency) => {
    if (!rawData || rawData.length === 0) return [];

    const dailySeries = normaliseSeries(rawData);
    if (frequency === 'daily') return dailySeries;

    const aggregated = [];

    if (frequency === 'weekly') {
      // Group by week using ISO week number
      const weekGroups = {};
      dailySeries.forEach(item => {
        const date = new Date(item.date);
        // ISO-Wochenjahr (Jahr des Donnerstags) statt Kalenderjahr: sonst landet
        // z. B. der 31.12.2025 (KW 1/2026) im Bucket "2025-W1" zusammen mit Januar 2025.
        const thursday = new Date(date);
        thursday.setDate(date.getDate() + 4 - (date.getDay() || 7));
        const year = thursday.getFullYear();
        const weekNum = getISOWeek(date);
        const weekKey = `${year}-W${String(weekNum).padStart(2, '0')}`;

        if (!weekGroups[weekKey]) {
          weekGroups[weekKey] = {
            items: [],
            firstDate: date,
            year: year,
            weekNum: weekNum
          };
        }
        weekGroups[weekKey].items.push(item);
      });

      // Calculate weekly averages from existing datapoints only (no gap-filling).
      const sortedWeekKeys = Object.keys(weekGroups).sort();
      const labelForWeek = (year, weekNum) => {
        // Approximate first day of ISO week (Monday)
        const simple = new Date(year, 0, 4 + (weekNum - 1) * 7);
        const dayOfWeek = simple.getDay() || 7;
        const monday = new Date(simple);
        monday.setDate(simple.getDate() - dayOfWeek + 1);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[monday.getMonth()]} ${monday.getDate()}`;
      };

      sortedWeekKeys.forEach((key) => {
        const weekGroup = weekGroups[key];
        if (!weekGroup?.items?.length) return;
        const weekData = weekGroup.items;
        const numericValues = weekData
          .map((item) => Number(item.value))
          .filter((v) => Number.isFinite(v));
        const avgValue = numericValues.length
          ? numericValues.reduce((sum, value) => sum + value, 0) / numericValues.length
          : null;
        const totalVolume = weekData.reduce((sum, item) => sum + (Number.isFinite(Number(item?.volume)) ? Number(item.volume) : 0), 0);
        const sample = weekData[0];
        aggregated.push({
          ...sample,
          value: avgValue,
          open: avgValue,
          high: avgValue,
          low: avgValue,
          close: avgValue,
          volume: totalVolume,
          xAxisLabel: labelForWeek(weekGroup.year, weekGroup.weekNum),
          date: sample?.date,
        });
      });
    } else if (frequency === 'monthly') {
      // Group by month
      const monthGroups = {};
      dailySeries.forEach(item => {
        const date = new Date(item.date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        if (!monthGroups[monthKey]) {
          monthGroups[monthKey] = {
            items: [],
            year: date.getFullYear(),
            month: date.getMonth()
          };
        }
        monthGroups[monthKey].items.push(item);
      });

      const sortedMonthKeys = Object.keys(monthGroups).sort();

      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      sortedMonthKeys.forEach((key) => {
        const monthGroup = monthGroups[key];
        if (!monthGroup?.items?.length) return;
        const monthData = monthGroup.items;
        const numericValues = monthData
          .map((item) => Number(item.value))
          .filter((v) => Number.isFinite(v));
        const avgValue = numericValues.length
          ? numericValues.reduce((sum, value) => sum + value, 0) / numericValues.length
          : null;
        const totalVolume = monthData.reduce((sum, item) => sum + (Number.isFinite(Number(item?.volume)) ? Number(item.volume) : 0), 0);
        const sample = monthData[0];
        aggregated.push({
          ...sample,
          value: avgValue,
          open: avgValue,
          high: avgValue,
          low: avgValue,
          close: avgValue,
          volume: totalVolume,
          xAxisLabel: monthNames[monthGroup.month],
          date: sample?.date,
        });
      });
    }

    return aggregated;
  }, [normaliseSeries]);

  // Process and merge chart data
  const data = useMemo(() => {
    // If data is still loading or there's an error, return empty array
    if (!effectiveChartSeriesData || !effectiveChartSeriesData.data) {
      return [];
    }

    let mainData = effectiveChartSeriesData.data;

    // Apply frequency aggregation (Werte kommen bereits umgerechnet vom Backend)
    mainData = aggregateDataByFrequency(mainData, selectedFrequency);
    const addNewsCounts = (rows = []) =>
      rows.map((row) => {
        const mainVolume = Number(row?.volume);
        const totalNewsCount = comparisons.reduce((sum, _comparison, index) => {
          const comparisonVolume = Number(row?.[`comparison_${index}_volume`]);
          return sum + (Number.isFinite(comparisonVolume) && comparisonVolume > 0 ? comparisonVolume : 0);
        }, Number.isFinite(mainVolume) && mainVolume > 0 ? mainVolume : 0);

        return {
          ...row,
          news_count: totalNewsCount,
          news_bar: totalNewsCount > 0 ? totalNewsCount : null,
        };
      });

    // If no comparisons or no comparison data, return main data only
    if (comparisons.length === 0 || !comparisonData) {
      // Apply mode transformation if needed
      if (chartMode === 'percent') {
        const base = mainData.find((d) => d.value !== null && d.value !== undefined)?.value || null;
        if (base) {
          return addNewsCounts(mainData.map((d) => {
            const toPct = (val) => {
              if (val === null || val === undefined) return null;
              return (val / base - 1) * 100;
            };
            return {
              ...d,
              value: toPct(d.value),
              open: toPct(d.open ?? d.value),
              high: toPct(d.high ?? d.value),
              low: toPct(d.low ?? d.value),
              close: toPct(d.close ?? d.value),
            };
          }));
        }
      }
      return addNewsCounts(mainData);
    }

    // Merge comparison data with main data
    // Create a map of main data by date for quick lookup
    const dataByDate = new Map(mainData.map(item => [item.date, { ...item }]));

    // Merge each comparison material's data
    comparisons.forEach((comparison, index) => {
      const comparisonSeries = comparisonData[comparison.code];

      if (!comparisonSeries || !Array.isArray(comparisonSeries)) {
        console.warn(`[MainChart] No data found for comparison material: ${comparison.code}`);
        return;
      }

      // Apply frequency aggregation to comparison data
      const aggregatedComparisonData = aggregateDataByFrequency(comparisonSeries, selectedFrequency);

      // Merge comparison values by date
      aggregatedComparisonData.forEach(compItem => {
        const existing = dataByDate.get(compItem.date);
        if (existing) {
          existing[`comparison_${index}_value`] = compItem.value;
          existing[`comparison_${index}_open`] = compItem.open;
          existing[`comparison_${index}_high`] = compItem.high;
          existing[`comparison_${index}_low`] = compItem.low;
          existing[`comparison_${index}_close`] = compItem.close;
          existing[`comparison_${index}_volume`] = Number.isFinite(Number(compItem?.volume)) ? Number(compItem.volume) : 0;
          existing[`comparison_${index}_currency`] = compItem.currency;
          existing[`comparison_${index}_value_unit`] = compItem.value_unit || compItem.unit;
        } else {
          // If main data doesn't have this date, add it
          dataByDate.set(compItem.date, {
            ...compItem,
            value: null, // Main material has no data for this date
            [`comparison_${index}_value`]: compItem.value,
            [`comparison_${index}_open`]: compItem.open,
            [`comparison_${index}_high`]: compItem.high,
            [`comparison_${index}_low`]: compItem.low,
            [`comparison_${index}_close`]: compItem.close,
            [`comparison_${index}_volume`]: Number.isFinite(Number(compItem?.volume)) ? Number(compItem.volume) : 0,
            [`comparison_${index}_currency`]: compItem.currency,
            [`comparison_${index}_value_unit`]: compItem.value_unit || compItem.unit,
          });
        }
      });
    });

    // Convert map back to array and sort by date
    let merged = Array.from(dataByDate.values()).sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    merged = addNewsCounts(merged);

    if (chartMode === 'percent') {
      const mainBase = merged.find((d) => d.value !== null && d.value !== undefined)?.value ?? null;
      const comparisonBases = comparisons.map((_, index) => {
        const key = `comparison_${index}_value`;
        const baseItem = merged.find((d) => d[key] !== null && d[key] !== undefined);
        return baseItem ? baseItem[key] : null;
      });

      merged = merged.map((d) => {
        const toPct = (val, base) => {
          if (val === null || val === undefined || base === null) return null;
          return (val / base - 1) * 100;
        };
        const next = {
          ...d,
          value: toPct(d.value, mainBase),
          open: toPct(d.open ?? d.value, mainBase),
          high: toPct(d.high ?? d.value, mainBase),
          low: toPct(d.low ?? d.value, mainBase),
          close: toPct(d.close ?? d.value, mainBase),
        };

        comparisons.forEach((comparison, index) => {
          const compKey = `comparison_${index}_value`;
          const compOpenKey = `comparison_${index}_open`;
          const compHighKey = `comparison_${index}_high`;
          const compLowKey = `comparison_${index}_low`;
          const compCloseKey = `comparison_${index}_close`;

          const baseVal = comparisonBases[index];
          next[compKey] = toPct(d[compKey], baseVal);
          next[compOpenKey] = toPct(d[compOpenKey] ?? d[compKey], baseVal);
          next[compHighKey] = toPct(d[compHighKey] ?? d[compKey], baseVal);
          next[compLowKey] = toPct(d[compLowKey] ?? d[compKey], baseVal);
          next[compCloseKey] = toPct(d[compCloseKey] ?? d[compKey], baseVal);
        });

        return next;
      });
    }

    return merged;
  }, [
    effectiveChartSeriesData,
    comparisons,
    comparisonData,
    selectedFrequency,
    aggregateDataByFrequency,
    chartMode,
  ]);

  const mainDisplaySeries = useMemo(() => {
    if (!effectiveChartSeriesData || !effectiveChartSeriesData.data) return [];
    return [...effectiveChartSeriesData.data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [effectiveChartSeriesData]);

  const mainConverted = Boolean(mainMeta.display?.converted);

  // Letzter Kurs laut /general (juengster Tagesschluss inkl. Vortagesaenderung).
  // Der Kopf darf nicht vom sichtbaren Chartfenster abhaengen: In der 1J-Ansicht
  // (Monatsbuckets) war der letzte Punkt das Ende des Vormonats, der Kopf zeigte
  // dadurch z. B. den Kurs vom 30.09. statt vom 21.10.
  // /general ist immer in Originalwaehrung/-einheit und gilt daher nur ohne Umrechnung.
  const generalLatestPoints = useMemo(() => {
    const value = Number(material?.last_price_value);
    const dateIso = material?.last_price_date_iso;
    // 0 ist in den Tagesdaten eine Luecke, kein Kurs (siehe Backend-Filter close_value > 0).
    if (material?.last_price_value == null || !Number.isFinite(value) || value === 0 || !dateIso) return null;
    const changeAbs = Number(material?.change_abs_value);
    const raw = [];
    if (material?.change_abs_value != null && Number.isFinite(changeAbs)) {
      raw.push({ date: null, value: value - changeAbs });
    }
    raw.push({ date: dateIso, value });
    return raw;
  }, [material]);

  // Bei Umrechnung: Kopf aus der umgerechneten Tagesreihe (gesamte Historie,
  // sonst das sichtbare Fenster), nie aus /general.
  const convertedHeaderPoints = useMemo(() => {
    if (!mainConverted) return null;
    const source = isFullRangeSeriesReady && fullRangeSeriesData?.meta?.converted
      ? fullRangeSeriesData.data
      : mainDisplaySeries;
    const latest = latestChangeFromSeries(source);
    if (!latest) return null;
    return latest.previous !== null
      ? [{ date: null, value: latest.previous }, { date: latest.date, value: latest.value }]
      : [{ date: latest.date, value: latest.value }];
  }, [mainConverted, isFullRangeSeriesReady, fullRangeSeriesData, mainDisplaySeries]);

  useEffect(() => {
    if (typeof onHeaderDataChange !== 'function') return;
    if (!material) return;

    let latest = mainDisplaySeries?.[mainDisplaySeries.length - 1];
    let previous = mainDisplaySeries?.[mainDisplaySeries.length - 2];

    if (mainConverted) {
      latest = convertedHeaderPoints?.[convertedHeaderPoints.length - 1];
      previous = convertedHeaderPoints?.length > 1 ? convertedHeaderPoints[0] : null;
    } else if (generalLatestPoints?.length) {
      const generalTs = new Date(generalLatestPoints[generalLatestPoints.length - 1].date).getTime();
      const seriesTs = latest?.date ? new Date(latest.date).getTime() : NaN;
      if (Number.isFinite(generalTs) && (!Number.isFinite(seriesTs) || generalTs >= seriesTs)) {
        latest = generalLatestPoints[generalLatestPoints.length - 1];
        previous = generalLatestPoints.length > 1 ? generalLatestPoints[0] : null;
      }
    }

    if (!latest || latest.value === null || latest.value === undefined || Number.isNaN(Number(latest.value))) {
      onHeaderDataChange(null);
      return;
    }

    const currentValue = Number(latest.value);
    const previousValue = previous?.value;
    const hasPrevious = previousValue !== null && previousValue !== undefined && Number.isFinite(Number(previousValue));
    const absChange = hasPrevious ? currentValue - Number(previousValue) : null;
    const pctChange = hasPrevious && Number(previousValue) !== 0
      ? (absChange / Number(previousValue)) * 100
      : null;

    const dateDisplay = latest?.date
      ? new Date(latest.date).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
      : material?.last_price_date_display;

    onHeaderDataChange({
      ...material,
      last_price_display: formatDisplayValueWithCode(currentValue, mainMeta.currency, mainMeta.unit, 2, numberLocale),
      last_price_value: currentValue,
      last_price_date_display: dateDisplay,
      change_abs_value: absChange,
      change_pct_value: pctChange,
      currency: mainMeta.currency,
      value_unit: mainMeta.unit,
      number_format: numberFormat,
      main_color: mainMeta.color || '#8B5CF6',
    });
  }, [onHeaderDataChange, material, mainDisplaySeries, generalLatestPoints, convertedHeaderPoints, mainConverted, mainMeta.currency, mainMeta.unit, mainMeta.color, locale, numberLocale, numberFormat]);

  // Kopfzeilen der Vergleichsreihen: bei Umrechnung letzter Kurs und Aenderung
  // aus der umgerechneten Tagesreihe (letzte ~13 Monate), sonst bleibt /general.
  useEffect(() => {
    if (typeof onComparisonHeaderDataChange !== 'function') return undefined;
    const groups = groupComparisonRequests(comparisons, (c) => comparisonTargets[c.code])
      .filter((group) => hasConversionTarget(group.target));
    if (!materialCode || !groups.length) {
      onComparisonHeaderDataChange({});
      return undefined;
    }
    let cancelled = false;
    const end = new Date();
    const start = new Date(end.getTime() - 400 * 24 * 60 * 60 * 1000);
    Promise.all(groups.map((group) => (
      materialService.getChartComparison(materialCode, {
        codes: group.codes.join(','),
        start: start.toISOString(),
        end: end.toISOString(),
        tf: '1d',
        ...group.params,
      }).then((response) => [group, response]).catch(() => [group, null])
    ))).then((results) => {
      if (cancelled) return;
      const out = {};
      results.forEach(([group, response]) => {
        group.codes.forEach((code) => {
          const general = comparisonGeneralByCode[code];
          const display = resolveSeriesDisplay({
            sourceCurrency: general?.currency,
            sourceUnit: general?.value_unit,
            target: group.target,
            meta: response?.meta?.[code],
          });
          if (!display.converted) return;
          const latest = latestChangeFromSeries(response?.series?.[code]);
          if (!latest) return;
          out[code] = {
            last_price_value: latest.value,
            change_abs_value: latest.absChange,
            change_pct_value: latest.pctChange,
            last_price_date_display: new Date(latest.date).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }),
            currency: display.currency,
            value_unit: display.unit,
          };
        });
      });
      onComparisonHeaderDataChange(out);
    });
    return () => { cancelled = true; };
    // comparisonTargets steckt in comparisonTargetsKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onComparisonHeaderDataChange, comparisons, comparisonTargetsKey, comparisonGeneralByCode, materialCode, locale]);

  // Process one forecast series to align with chart (aggregated, converted)
  // Die Prognose kommt nur in Originalwaehrung/-einheit. Fuer eine umgerechnete
  // Reihe wird sie ausgeblendet statt clientseitig umgerechnet.
  const processForecastSeries = useCallback((rawForecast = []) => {
    if (!rawForecast || !rawForecast.length) return [];
    const series = aggregateDataByFrequency(rawForecast.map((f) => ({
      ...f,
      date: f.date,
      value: f.value,
    })), selectedFrequency);
    return series.map((d) => ({
      ...d,
      ts: new Date(d.date).getTime(),
    }));
  }, [aggregateDataByFrequency, selectedFrequency]);

  const rawMainForecast = useMemo(
    () => processForecastSeries(forecastDataMap[materialCode] || []),
    [forecastDataMap, materialCode, processForecastSeries]
  );
  const processedForecast = useMemo(() => (mainConverted ? [] : rawMainForecast), [mainConverted, rawMainForecast]);

  const { comparisonForecastMap, comparisonForecastSuppressed } = useMemo(() => {
    const out = {};
    let suppressed = false;
    comparisons.forEach((comparison, index) => {
      const series = processForecastSeries(forecastDataMap[comparison.code] || []);
      const converted = Boolean(getSeriesMeta(comparison.code, comparison.id).display?.converted);
      if (converted && series.length) suppressed = true;
      out[index] = converted ? [] : series;
    });
    return { comparisonForecastMap: out, comparisonForecastSuppressed: suppressed };
  }, [comparisons, forecastDataMap, getSeriesMeta, processForecastSeries]);
  const forecastSuppressed = showForecast
    && ((mainConverted && rawMainForecast.length > 0) || comparisonForecastSuppressed);

  // Ensure numeric timestamps for stable X axis (prevents cursor jump) and merge forecast
  const chartData = useMemo(() => {
    const base = (data || []).map((d) => {
      const ts = new Date(d.date).getTime();
      return { ...d, ts };
    });

    const mergedMap = new Map();
    base.forEach((d) => mergedMap.set(d.ts, { ...d }));

    if (showForecast && processedForecast.length) {
      processedForecast.forEach((f) => {
        const existing = mergedMap.get(f.ts) || {};
        mergedMap.set(f.ts, { ...existing, ...f, forecast_value: f.value });
      });
    }
    if (showForecast && comparisons.length) {
      comparisons.forEach((comparison, index) => {
        const series = comparisonForecastMap[index] || [];
        series.forEach((f) => {
          const existing = mergedMap.get(f.ts) || {};
          mergedMap.set(f.ts, {
            ...existing,
            [`comparison_${index}_forecast_value`]: f.value,
          });
        });
      });
    }

    return Array.from(mergedMap.values()).sort((a, b) => a.ts - b.ts);
  }, [data, processedForecast, showForecast, comparisons, comparisonForecastMap]);

  const hasAnyForecastData = useMemo(() => {
    if (rawMainForecast.length > 0) return true;
    return comparisons.some((c) => (forecastDataMap[c.code] || []).length > 0);
  }, [rawMainForecast, comparisons, forecastDataMap]);

  const forecastUnavailable = forecastRequested && !hasAnyForecastData && !forecastLoading;

  const conversionNoteEntries = useMemo(() => {
    const entries = [];
    // Erst nach einer Antwort beurteilen (nicht waehrend des ersten Ladens)
    if (effectiveChartSeriesData) {
      entries.push({ key: materialCode, name: material?.name || mainMeta.name, display: mainMeta.display, meta: mainMeta.responseMeta });
    }
    displayComparisons.forEach((comparison) => {
      const compMeta = getSeriesMeta(comparison.code, comparison.id);
      if (!comparisonMetaByCode[comparison.code]) return;
      entries.push({ key: comparison.code, name: comparison.name || compMeta.name, display: compMeta.display, meta: compMeta.responseMeta });
    });
    return entries;
  }, [effectiveChartSeriesData, materialCode, material, mainMeta, displayComparisons, getSeriesMeta, comparisonMetaByCode]);

  // Handle custom date range selection
  const handleCustomDateRange = () => {
    if (!customStartDate || !customEndDate) return;

    const startTime = customStartDate.getTime();
    const endTime = customEndDate.getTime();

    if (!fullHistoricalData.length) return;

    const firstDataTime = fullHistoricalData[0].timestamp;
    const lastDataTime = fullHistoricalData[fullHistoricalData.length - 1].timestamp;
    const totalTimeSpan = lastDataTime - firstDataTime;

    // Calculate position as percentage of total data
    const startPosition = Math.max(0, (startTime - firstDataTime) / totalTimeSpan);
    const endPosition = Math.min(1, (endTime - firstDataTime) / totalTimeSpan);

    const toDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const nextSelection = { start: startPosition, end: endPosition };
    setSelectionRange(nextSelection);
    setAppliedCustomRange({ start: toDay(customStartDate), end: toDay(customEndDate), selection: nextSelection });
    setSelectedTimeFrame('custom');
    setShowDatePicker(false);
  };

  // Handle timeframe button clicks - update selection range
  const handleTimeFrameClick = (timeFrameId) => {
    setSelectedTimeFrame(timeFrameId);
    
    if (!fullHistoricalData.length) return;
    
    // Get the total time span of our historical data (in milliseconds)
    const firstDataPoint = fullHistoricalData[0].timestamp;
    const lastDataPoint = fullHistoricalData[fullHistoricalData.length - 1].timestamp;
    const totalTimeSpan = lastDataPoint - firstDataPoint;
    
    let newSelectionRange;
    let timeframeDurationMs = 0;
    
    if (timeFrameId === 'max') {
      // Show all available data
      newSelectionRange = { start: 0.0, end: 1.0 };
    } else {
      // Calculate timeframe duration in milliseconds
      
      
      switch (timeFrameId) {
        case '1h': timeframeDurationMs = 1 * 60 * 60 * 1000; break;
        case '3h': timeframeDurationMs = 3 * 60 * 60 * 1000; break;
        case '1d': timeframeDurationMs = 1 * 24 * 60 * 60 * 1000; break;
        case '1w': timeframeDurationMs = 7 * 24 * 60 * 60 * 1000; break;
        case '1m': timeframeDurationMs = 30 * 24 * 60 * 60 * 1000; break;
        case '3m': timeframeDurationMs = 90 * 24 * 60 * 60 * 1000; break;
        case '1y': timeframeDurationMs = 365 * 24 * 60 * 60 * 1000; break;
        case '5y': timeframeDurationMs = 5 * 365 * 24 * 60 * 60 * 1000; break;
        default: timeframeDurationMs = 365 * 24 * 60 * 60 * 1000; // Default to 1 year
      }
      
      // Calculate what percentage of our total historical data this represents
      let percentageOfTotal = timeframeDurationMs / totalTimeSpan;
      
      // Ensure minimum visible width of 2% for very short timeframes
      const minVisiblePercentage = 0.02; // 2%
      percentageOfTotal = Math.max(minVisiblePercentage, Math.min(1.0, percentageOfTotal));
      
      // Keep the selection near the end but allow for navigation
      // For short timeframes, position it at 90% so there's room to drag left and right
      let endPosition = 1.0;
      if (percentageOfTotal < 0.1) { // If less than 10% of total data
        endPosition = 0.95; // Leave some room for dragging right
      }
      
      newSelectionRange = {
        start: Math.max(0, endPosition - percentageOfTotal),
        end: endPosition
      };
    }
    
    console.log(`Timeframe: ${timeFrameId}, Duration: ${timeframeDurationMs/1000/60/60/24} days, Selection: ${(newSelectionRange.start * 100).toFixed(2)}% - ${(newSelectionRange.end * 100).toFixed(2)}%`);
    setSelectionRange(newSelectionRange);
  };
  
  const currentValue = data[data.length - 1]?.value || 0;
  const previousValue = data[data.length - 2]?.value || 0;
  const changePercent = ((currentValue - previousValue) / previousValue * 100).toFixed(2);
  const isPositive = parseFloat(changePercent) >= 0;

  // Loading state (include comparison loading)
  const isLoading = overviewLoading || seriesLoading || comparisonLoading;
  const hasError = overviewError || seriesError || comparisonError;

  return (
    <div style={{
      background: 'transparent',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      padding: '0'
    }}>

        {/* Error Display */}
        {hasError && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px',
            color: '#EF4444',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <AlertTriangle size={20} />
            <div>
              <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                {overviewError ? t('charts.error.historical') : t('charts.error.chart')}
              </div>
              <div style={{ fontSize: '14px', opacity: 0.9 }}>
                {overviewError?.message || seriesError?.message || t('charts.error.tryAgainLater')}
              </div>
            </div>
          </div>
        )}

        {/* Top Toolbar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', position: 'relative' }}>
            <button
              className="chart-btn chart-btn-icon-only"
              onClick={() => setShowSettingsPopup(!showSettingsPopup)}
            >
              <SlidersHorizontal size={18} />
            </button>

            {/* Chart Settings Popup */}
            <ChartSettings
              isOpen={showSettingsPopup}
              onClose={() => setShowSettingsPopup(false)}
              comparisons={comparisons}
              materialCode={materialCode}
              material={material}
              mode={chartMode}
              numberFormat={numberFormat}
              initialSettings={chartSettingsState}
              onApply={(payload) => {
                if (payload?.mode) setChartMode(payload.mode);
                if (payload?.settings) setChartSettingsState(payload.settings);
                if (payload?.numberFormat) setNumberFormat(normaliseNumberFormat(payload.numberFormat));
                setShowSettingsPopup(false);
              }}
            />
            
            {/* Chart type icons */}
            <div className="chart-type-selector">
              {[
                { type: 'area', icon: AreaChart },
                { type: 'line', icon: LineChart },
                { type: 'bar', icon: BarChart3 },
                // { type: 'candlestick', icon: CandlestickChart }, // temporarily hidden due to data constraints
              ].map(({ type, icon: Icon }) => (
                <button
                  key={type}
                  onClick={() => setSelectedChartType(type)}
                  className={`chart-btn chart-btn-icon-only ${selectedChartType === type ? 'active' : ''}`}
                >
                  <Icon size={18} />
                </button>
              ))}
            </div>

            <div style={{ position: 'relative' }}>
              <button
                className="chart-btn chart-btn-styled"
                onClick={() => setShowFrequencyPopup(!showFrequencyPopup)}
              >
                <span>{t('charts.frequency')}</span>
                <ChevronDown size={16} />
              </button>

              {/* Frequency Popup positioned relative to Frequency button */}
              <FrequencyPopup
                isOpen={showFrequencyPopup}
                onClose={() => setShowFrequencyPopup(false)}
                selectedFrequency={selectedFrequency}
                onFrequencyChange={setSelectedFrequency}
              />
            </div>
            {/*
            <button className="chart-btn chart-btn-styled">
              <AlertTriangle size={16} />
              <span>Setup alert</span>
            </button>
            */}
            <div style={{ position: 'relative' }}>
              <button
                className="chart-btn chart-btn-styled"
                onClick={() => setShowExportPopup(true)}
              >
                <Download size={16} />
                <span>{t('charts.export')}</span>
              </button>

              {/* Export Popup positioned relative to Export button */}
              <ExportPopup
                isOpen={showExportPopup}
                onClose={() => setShowExportPopup(false)}
                materialName={material?.name}
                materialDate={material?.last_price_date_display}
                materialPrice={material?.last_price_display}
                materialCode={materialCode}
                startDate={startDate}
                endDate={endDate}
                timeframe={selectedTimeFrame}
                defaultFrequency={selectedFrequency}
                sourceCurrency={mainMeta?.baseCurrency}
                sourceUnit={mainMeta?.baseUnit}
                initialTarget={mainTarget}
              />
            </div>
          </div>

          <div style={{ flex: 1, display: 'flex', justifyContent: 'center', width: '100%' }}>
            <CompareDropdown
              placeholder={t('charts.compareTo')}
              onItemSelect={onAddComparison}
              maxReached={comparisons.length >= maxComparisons}
              existingComparisons={comparisons}
              currentMaterialCode={currentMaterialCode}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div className="chart-settings-container" style={{ position: 'relative' }}>
              <button
                className="chart-settings-btn chart-icon-btn"
                onClick={() => setShowForecastPopup(true)}
                style={{ position: 'relative', zIndex: 2 }}
              >
                <img src={GraphIcon} alt={t('charts.graph')} className="chart-icon-image" />
              </button>

              <button className="chart-settings-btn plus-icon-btn">
                <Plus size={18} style={{ position: 'relative', zIndex: 1, color: '#000', strokeWidth: 2.5 }} />
              </button>

            {/* Forecast Popup positioned relative to button */}
            <Forecast
              isOpen={showForecastPopup}
              onClose={() => setShowForecastPopup(false)}
              onApply={(opts) => {
                if (opts) {
                  setForecastOptions(opts);
                  setShowForecast(true);
                  setForecastRequested(true);
                }
                setShowForecastPopup(false);
              }}
            />
            </div>
          </div>
        </div>

        {/* Main Chart Container */}
        <div className="main-chart-container" style={{ position: 'relative' }}>
          {/* Chart Area */}
          <div ref={chartAreaRef} className="chart-area glass-card-ui" style={{ position: 'relative' }}>
            {/* Time Frame Selector */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              marginBottom: '24px',
              flexWrap: 'wrap',
              gap: '12px'
            }}>
              <div className="timeframe-pills-container">
                {visibleTimeFrames.map((tf) => (
                  <button
                    key={tf.id}
                    onClick={() => handleTimeFrameClick(tf.id)}
                    className={`timeframe-pill ${selectedTimeFrame === tf.id ? 'active' : ''}`}
                  >
                    {tf.label}
                  </button>
                ))}
              </div>

              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className={`date-selector-pill ${showDatePicker ? 'active' : ''}`}
                >
                  <Calendar size={16} />
                  <span>
                    {selectedTimeFrame === 'custom' ? t('charts.customRange') : t('charts.selectDateRange')}
                  </span>
                </button>

                {/* Date Picker Dropdown */}
                {showDatePicker && (
                  <div className="custom-datepicker-wrapper">
                    <div className="custom-datepicker-field-container">
                      <label className="custom-datepicker-label">
                        {t('charts.startDate')}
                      </label>
                      <DatePicker
                        selected={customStartDate}
                        onChange={(date) => setCustomStartDate(date)}
                        selectsStart
                        startDate={customStartDate}
                        endDate={customEndDate}
                        minDate={fullHistoricalData[0] ? new Date(fullHistoricalData[0].date) : null}
                        maxDate={fullHistoricalData[fullHistoricalData.length - 1] ? new Date(fullHistoricalData[fullHistoricalData.length - 1].date) : null}
                        dateFormat="dd/MM/yyyy"
                        placeholderText={t('charts.datePlaceholder')}
                        className="custom-datepicker-input"
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                      />
                    </div>

                    <div className="custom-datepicker-field-container">
                      <label className="custom-datepicker-label">
                        {t('charts.endDate')}
                      </label>
                      <DatePicker
                        selected={customEndDate}
                        onChange={(date) => setCustomEndDate(date)}
                        selectsEnd
                        startDate={customStartDate}
                        endDate={customEndDate}
                        minDate={customStartDate || (fullHistoricalData[0] ? new Date(fullHistoricalData[0].date) : null)}
                        maxDate={fullHistoricalData[fullHistoricalData.length - 1] ? new Date(fullHistoricalData[fullHistoricalData.length - 1].date) : null}
                        dateFormat="dd/MM/yyyy"
                        placeholderText={t('charts.datePlaceholder')}
                        className="custom-datepicker-input"
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                      />
                    </div>

                    <div className="custom-datepicker-actions">
                      <button
                        onClick={() => setShowDatePicker(false)}
                        className="datepicker-btn datepicker-btn-cancel"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        onClick={handleCustomDateRange}
                        disabled={!customStartDate || !customEndDate}
                        className="datepicker-btn datepicker-btn-apply"
                      >
                        {t('charts.apply')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div style={{ height: '400px', width: '100%' }}>
              {isLoading ? (
                <ChartSkeleton height="400px" showLegend={true} />
              ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 12, right: 0, left: 0, bottom: 16 }} wrapperStyle={{ outline: 'none' }}>
                  <defs>
                    <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={mainColor} stopOpacity={0.6} />
                      <stop offset="50%" stopColor={mainColor} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={mainColor} stopOpacity={0.08} />
                    </linearGradient>
                  </defs>
                  
                  <CartesianGrid strokeDasharray="1 1" stroke="#374151" opacity={0.2} />
                  
                  <XAxis
                    dataKey="ts"
                    type="number"
                    scale="time"
                    domain={[(v) => v, (v) => v]}
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fill: '#9CA3AF',
                      fontSize: 12,
                    }}
                    tickFormatter={(ts) => {
                      const d = new Date(ts);
                      return d.toLocaleDateString(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' });
                    }}
                    tickMargin={6}
                    interval="preserveStartEnd"
                    minTickGap={30}
                    padding={{ left: 0, right: 0 }}
                  />
                  
                  <YAxis
                    yAxisId="price"
                    orientation="left"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#9CA3AF', fontSize: 12 }}
                    tickFormatter={yTickFormatter}
                    domain={[(dataMin) => {
                      // Calculate 10% padding below the minimum based on the actual data range
                      const padding = Math.abs(dataMin) * 0.1;
                      return Math.floor(dataMin - padding);
                    }, (dataMax) => {
                      // Calculate 10% padding above the maximum based on the actual data range
                      const padding = Math.abs(dataMax) * 0.1;
                      return Math.ceil(dataMax + padding);
                    }]}
                    tickCount={6}
                    allowDecimals={false}
                  />
                  
                  {showNews && (
                    <YAxis 
                      yAxisId="news"
                      hide
                      width={0}
                      orientation="right"
                      axisLine={false}
                      tickLine={false}
                      tick={{ display: 'none' }}
                      domain={[0, (dataMax) => Math.max(3, Number(dataMax || 0) + 1)]}
                    />
                  )}

                  <Tooltip
                    content={
	                      <CustomTooltip
	                        chartType={selectedChartType}
	                        comparisons={displayComparisons}
	                        materialName={material?.name}
	                        materialCode={materialCode}
	                        mode={chartMode}
	                        getSeriesMeta={getSeriesMeta}
                          t={t}
                          locale={locale}
                          numberLocale={numberLocale}
                          mainColor={mainColor}
                          forecastColor={forecastColor}
                          showForecast={showForecast}
	                      />
                    }
                    cursor={{ stroke: mainColor, strokeWidth: 1, strokeDasharray: '5 5' }}
                    animationDuration={150}
                    isAnimationActive={true}
                    position={{ y: 0 }}
                    allowEscapeViewBox={{ x: false, y: true }}
                  />

                  {selectedChartType === 'area' && (
                    <>
                      {showValue && (
                        <Area
                          yAxisId="price"
                          type="stepAfter"
                          connectNulls
                          dataKey="value"
                          stroke={mainColor}
                          strokeWidth={2}
                          fill="url(#areaGradient)"
                          dot={false}
                          activeDot={{ r: 4, fill: mainColor, stroke: '#fff', strokeWidth: 2 }}
                        />
                      )}
                      {/* Render comparison areas */}
                      {showValue &&
                        displayComparisons.map((comparison, index) => (
                          <Area
                            key={comparison.id}
                            yAxisId="price"
                            type="stepAfter"
                            connectNulls
                            dataKey={`comparison_${index}_value`}
                            stroke={comparison.color}
                            strokeWidth={2}
                            fill={comparison.color}
                            fillOpacity={0.1}
                            dot={false}
                            activeDot={{ r: 4, fill: comparison.color, stroke: '#fff', strokeWidth: 2 }}
                          />
                        ))}

                      {showForecast && processedForecast.length > 0 && (
                        <Line
                          yAxisId="price"
                          type="stepAfter"
                          connectNulls
                          dataKey="forecast_value"
                          stroke={forecastColor}
                          strokeWidth={1}
                          strokeDasharray="3 3"
                          strokeOpacity={0.8}
                          dot={{ r: 1.5, stroke: forecastColor, fill: forecastColor }}
                          activeDot={{ r: 2.5, fill: forecastColor, stroke: '#111827', strokeWidth: 0.6 }}
                        />
                      )}
                      {showForecast &&
                        displayComparisons.map((comparison, index) => (
                          <Line
                            key={`${comparison.id}-forecast`}
                            yAxisId="price"
                            type="stepAfter"
                            connectNulls
                            dataKey={`comparison_${index}_forecast_value`}
                            stroke={comparison.color}
                            strokeWidth={1}
                            strokeDasharray="4 4"
                            strokeOpacity={0.85}
                            dot={false}
                            activeDot={{ r: 2.5, fill: comparison.color, stroke: '#111827', strokeWidth: 0.6 }}
                          />
                        ))}
                    </>
                  )}
                  
                  {selectedChartType === 'line' && (
                    <>
                      {showValue && (
                        <Line
                          yAxisId="price"
                          type="stepAfter"
                          connectNulls
                          dataKey="value"
                          stroke={mainColor}
                          strokeWidth={2}
                          dot={false}
                          activeDot={{ r: 4, fill: mainColor, stroke: '#fff', strokeWidth: 2 }}
                        />
                      )}
                      {/* Render comparison lines */}
                      {showValue &&
                        displayComparisons.map((comparison, index) => (
                          <Line
                            key={comparison.id}
                            yAxisId="price"
                            type="stepAfter"
                            connectNulls
                            dataKey={`comparison_${index}_value`}
                            stroke={comparison.color}
                            strokeWidth={2}
                            dot={false}
                            activeDot={{ r: 4, fill: comparison.color, stroke: '#fff', strokeWidth: 2 }}
                          />
                        ))}

                      {showForecast && processedForecast.length > 0 && (
                        <Line
                          yAxisId="price"
                          type="stepAfter"
                          connectNulls
                          dataKey="forecast_value"
                          stroke={forecastColor}
                          strokeWidth={1}
                          strokeDasharray="3 3"
                          strokeOpacity={0.8}
                          dot={{ r: 1.5, stroke: forecastColor, fill: forecastColor }}
                          activeDot={{ r: 2.5, fill: forecastColor, stroke: '#111827', strokeWidth: 0.6 }}
                        />
                      )}
                      {showForecast &&
                        displayComparisons.map((comparison, index) => (
                          <Line
                            key={`${comparison.id}-forecast`}
                            yAxisId="price"
                            type="stepAfter"
                            connectNulls
                            dataKey={`comparison_${index}_forecast_value`}
                            stroke={comparison.color}
                            strokeWidth={1}
                            strokeDasharray="4 4"
                            strokeOpacity={0.85}
                            dot={false}
                            activeDot={{ r: 2.5, fill: comparison.color, stroke: '#111827', strokeWidth: 0.6 }}
                          />
                        ))}
                    </>
                  )}
                  
                  {selectedChartType === 'bar' && (
                    <>
                      {showValue && (
                        <Bar
                          yAxisId="price"
                          dataKey="value"
                          fill={mainColor}
                          opacity={0.8}
                          radius={[2, 2, 0, 0]}
                        />
                      )}
                      {/* Render comparison bars */}
                      {showValue &&
                        displayComparisons.map((comparison, index) => (
                          <Bar
                            key={comparison.id}
                            yAxisId="price"
                            dataKey={`comparison_${index}_value`}
                            fill={comparison.color}
                            opacity={0.6}
                            radius={[2, 2, 0, 0]}
                          />
                        ))}

                      {showForecast && processedForecast.length > 0 && (
                        <Line
                          yAxisId="price"
                          type="stepAfter"
                          connectNulls
                          dataKey="forecast_value"
                          stroke={forecastColor}
                          strokeWidth={1}
                          strokeDasharray="3 3"
                          strokeOpacity={0.8}
                          dot={{ r: 1.5, stroke: forecastColor, fill: forecastColor }}
                          activeDot={{ r: 2.5, fill: forecastColor, stroke: '#111827', strokeWidth: 0.6 }}
                        />
                      )}
                      {showForecast &&
                        displayComparisons.map((comparison, index) => (
                          <Line
                            key={`${comparison.id}-forecast`}
                            yAxisId="price"
                            type="stepAfter"
                            connectNulls
                            dataKey={`comparison_${index}_forecast_value`}
                            stroke={comparison.color}
                            strokeWidth={1}
                            strokeDasharray="4 4"
                            strokeOpacity={0.85}
                            dot={false}
                            activeDot={{ r: 2.5, fill: comparison.color, stroke: '#111827', strokeWidth: 0.6 }}
                          />
                        ))}
                    </>
                  )}
                  
                  {selectedChartType === 'candlestick' && null}
                  
                  {showNews && (
                    <Bar
                      yAxisId="news"
                      dataKey="news_bar"
                      fill="#F97316"
                      opacity={0.45}
                      radius={[0, 0, 0.1, 0.1]}
                      barSize={8}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
              )}
            </div>

            {forecastLoading && forecastRequested && (
              <div className="forecast-loading-overlay" aria-live="polite" aria-busy="true">
                <div className="forecast-loading-card">
                  <div className="forecast-loading-placeholder" aria-hidden="true" />
                  <div className="forecast-loading-placeholder forecast-loading-placeholder-short" aria-hidden="true" />
                  <span className="forecast-loading-text">
                    {t('charts.forecast')} ({t('common.loading')})
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Chart History Navigator */}
        {!overviewLoading && fullHistoricalData && fullHistoricalData.length > 0 && (
          <ChartHistoryNavigator
            fullData={fullHistoricalData}
            selectionRange={selectionRange}
            onSelectionChange={handleSelectionChange}
            selectedTimeFrame={selectedTimeFrame}
            onInteractionChange={setIsNavigatorInteracting}
            mainColor={mainColor}
          />
        )}

          {/* Bottom Legend */}
          <div className="chart-legend-container">
            <button
              onClick={() => setShowNews(!showNews)}
              className={`chart-legend-btn ${showNews ? 'active' : ''}`}
            >
              <div className="chart-legend-dot" style={{ borderColor: '#F97316' }} />
	              <span>{t('charts.showNews')}</span>
            </button>

            <div className={`chart-legend-group ${forecastUnavailable ? 'forecast-unavailable' : ''}`}>
              <button
                onClick={() => setShowValue(!showValue)}
                className={`chart-legend-btn ${showValue ? 'active' : ''}`}
              >
                <div className="chart-legend-dot" style={{ borderColor: mainColor }} />
	                <span>{t('charts.value')}</span>
              </button>

              {/* Render comparison legend items */}
              {/* {comparisons.map((comparison, index) => (
                <button
                  key={comparison.id}
                  className="chart-legend-btn active"
                >
                  <div className="chart-legend-dot" style={{ borderColor: comparison.color }} />
                  <span>{comparison.name.length > 20 ? comparison.name.substring(0, 20) + '...' : comparison.name}</span>
                </button>
              ))} */}

              <button
                onClick={() => {
                  if (forecastLoading) return;
                  if (!forecastRequested) {
                    setForecastRequested(true);
                    setShowForecast(true);
                    setForecastRequestNonce((n) => n + 1);
                    return;
                  }
                  if (!hasAnyForecastData) return;
                  setShowForecast((prev) => !prev);
                }}
                className={`chart-legend-btn ${showForecast ? 'active' : ''} ${forecastLoading ? 'disabled' : ''} ${!forecastRequested ? 'inactive' : ''}`}
                aria-disabled={forecastLoading}
                aria-busy={forecastLoading}
              >
                <div className="chart-legend-dot" style={{ borderColor: forecastColor }} />
                <span>
                  {t('charts.forecast')}
                  {forecastLoading ? ` (${t('common.loading')})` : ''}
                </span>
              </button>
            </div>
          </div>

          {/* Hinweis zur serverseitigen Umrechnung bzw. warum sie nicht moeglich war */}
          <ConversionNote entries={conversionNoteEntries} forecastSuppressed={forecastSuppressed} />
        </div>

    </div>
  );
}
